/**
 * Site footer: Midgard identity, AFTERRA attribution, community, legal and
 * support links. Desktop pages and public pages; on phones the same links
 * live on the About page (reached from the account menu).
 */
import { Link } from 'react-router-dom';
import { AFTERRA_URL, DISCORD_INVITE_URL } from '../../config/community';

export function DiscordGlyph({ className = 'w-4 h-4' }) {
  // Discord's mark (Simple Icons, CC0), used to identify the community link.
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M20.317 4.37a19.79 19.79 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.74 19.74 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.1 13.1 0 0 1-1.872-.892.077.077 0 0 1-.008-.128c.126-.094.252-.192.372-.291a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.009c.12.1.246.198.373.292a.077.077 0 0 1-.006.127 12.3 12.3 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.84 19.84 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
    </svg>
  );
}

const external = { target: '_blank', rel: 'noopener noreferrer' };

export default function SiteFooter({ tone = 'app', className = '' }) {
  const dark = tone === 'overlay';
  const muted = dark ? 'text-white/55' : 'text-muted-foreground';
  const head = dark ? 'text-white/80' : 'text-foreground/85';
  const link = `${muted} hover:text-[hsl(var(--mg-purple))] transition-colors`;
  const year = new Date().getFullYear();

  const col = (title, items) => (
    <div className="min-w-0">
      <h2 className={`text-[11px] uppercase tracking-[0.16em] font-semibold mb-3 ${head}`}>{title}</h2>
      <ul className="space-y-2 text-[13px]">{items}</ul>
    </div>
  );

  return (
    <footer className={`border-t ${dark ? 'border-white/10' : 'border-[hsl(var(--mg-steel)/0.15)]'} ${className}`} data-testid="site-footer">
      <div className="max-w-6xl mx-auto px-6 py-10 grid gap-8 grid-cols-2 md:grid-cols-[1.3fr_1fr_1fr_1fr]">
        <div className="col-span-2 md:col-span-1 min-w-0">
          <p className={`mg-wordmark text-base ${dark ? 'text-white' : 'text-foreground'}`} style={{ letterSpacing: '0.14em' }}>Midgard</p>
          <p className={`mt-2 text-[13px] leading-relaxed max-w-xs ${muted}`}>
            A Norse-inspired social universe of stories, friendships and adventures.
          </p>
          <p className={`mt-4 text-[13px] ${muted}`}>
            Created &amp; developed by{' '}
            <a href={AFTERRA_URL} {...external} className="font-semibold tracking-[0.12em] text-[hsl(var(--mg-purple))] hover:underline" data-testid="footer-afterra">AFTERRA</a>
          </p>
          <a
            href={DISCORD_INVITE_URL}
            {...external}
            className="mt-5 inline-flex items-center gap-2 h-9 px-3.5 rounded-lg text-[13px] font-semibold text-white bg-[#5865F2] hover:bg-[#4752c4] transition-colors"
            data-testid="footer-discord"
          >
            <DiscordGlyph /> Join our Discord
          </a>
          <p className={`mt-2 text-[12px] leading-snug max-w-xs ${muted}`}>
            Report bugs there: verified reports may earn 1 to 5 Runegold.
          </p>
        </div>

        {col('Discover', <>
          <li><Link to="/about" className={link}>About Midgard</Link></li>
          <li><a href={AFTERRA_URL} {...external} className={link}>The AFTERRA Komplex</a></li>
          <li><Link to="/about#referral" className={link}>Referral Program <span className="text-[11px] opacity-70">· Coming soon</span></Link></li>
          <li><Link to="/about#faq" className={link}>Questions</Link></li>
        </>)}

        {col('Community', <>
          <li><a href={DISCORD_INVITE_URL} {...external} className={link}>Official Discord</a></li>
          <li><Link to="/legal/runegold#bug-report-rewards" className={link}>Bug Reports &amp; Runegold Rewards</Link></li>
          <li><Link to="/legal/guidelines" className={link}>Community Guidelines</Link></li>
          <li><Link to="/about#support" className={link}>Support &amp; Contact</Link></li>
        </>)}

        {col('Legal & Privacy', <>
          <li><Link to="/legal/terms" className={link}>Terms of Service</Link></li>
          <li><Link to="/legal/privacy" className={link}>Privacy Policy</Link></li>
          <li><Link to="/legal/cookies" className={link}>Cookie Policy</Link></li>
          <li><Link to="/legal/cookies#preferences" className={link}>Manage Cookie Preferences</Link></li>
          <li><Link to="/legal/ai" className={link}>oDIN &amp; AI Notice</Link></li>
          <li><Link to="/legal/runegold" className={link}>Runegold Rules</Link></li>
        </>)}
      </div>
      <div className={`max-w-6xl mx-auto px-6 pb-8 text-[12px] ${muted}`}>
        © {year} Midgard. Created &amp; developed by AFTERRA.
      </div>
    </footer>
  );
}
